import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, MapPin, ShieldCheck, BookOpen } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import Seo, { breadcrumbLd, faqLd, ogUrl } from "@/components/site/Seo";
import LeadForm from "@/components/site/LeadForm";
import RiskCard from "@/components/site/RiskCard";
import { RelatedStrip } from "@/components/site/conversion";
import { CategoryTestimonials } from "@/components/site/Testimonials";
import { Depth } from "@/components/site/PublicPresentation";
import { getCategoryRisks } from "@/data/risks";
import { useContent } from "@/lib/useContent";
import { bySlug } from "@/data/categories";
import { CATEGORY_EXTRAS, CATEGORY_SERVICES, TIER_LABEL } from "@/data/categoryExtras";

const BASE = "https://thesourcingatlas.com";
const PROCESS = [
  ["01", "Send your requirement", "Product, specification, target volume and destination."],
  ["02", "Feasibility & costing", "Country recommendation and an indicative landed-cost band."],
  ["03", "Samples & approval", "Sealed samples, written terms and your sign-off."],
  ["04", "Production & shipping", "QC, AQL inspection and door-to-door delivery."],
];

export default function CategoryDetail() {
  const { slug } = useParams();
  const content = useContent();
  const base = bySlug(slug);
  if (!base) return <Navigate to="/categories" replace />;
  const ov = content.categories?.[slug];
  const c = ov ? { ...base, ...ov } : base;
  const isT1 = c.tier === 1;
  const extras = CATEGORY_EXTRAS[slug] || CATEGORY_EXTRAS.other;
  const [tierName, tierNote] = TIER_LABEL[c.tier] || ["Tell us what you need", "Share the requirement and we will say honestly whether we are the right partner."];
  const catRisks = (c.catRisks && c.catRisks.length) ? c.catRisks : (content.risks_by_category?.[slug] || getCategoryRisks(slug));

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: `${c.name} sourcing`,
    provider: { "@type": "Organization", name: "The Sourcing Atlas", url: BASE },
    areaServed: ["IN", "CN", "VN", "TH", "Worldwide"],
    description: c.metaDesc,
  };

  return (
    <SiteLayout>
      <Seo
        title={`${c.name} Sourcing — China, India, Vietnam & Thailand | The Sourcing Atlas`}
        description={c.metaDesc}
        path={`/categories/${c.slug}`}
        ogImage={ogUrl(`${c.name} Sourcing`, "What We Source", c.badge.replace("★", "").trim())}
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Categories", path: "/categories" },
            { name: c.name, path: `/categories/${c.slug}` },
          ]),
          serviceLd,
          faqLd(c.faq),
        ]}
      />
      <div className="crumb">
        <Link to="/">Home</Link> › <Link to="/categories">Categories</Link> › {c.name}
      </div>

      {/* HERO */}
      <header className="cat-hero" data-testid="category-hero">
        <picture className="cat-hero-photo">
          <source media="(max-width: 760px)" srcSet={`/images/categories/hero-${slug}-small.webp`} />
          <img src={`/images/categories/hero-${slug}.webp`} width="1800" height="780" alt={`${c.name} manufacturing and sourcing`} fetchPriority="high" />
        </picture>
        <div className="cat-hero-shade" />
        <div className="motif cat-hero-motif" dangerouslySetInnerHTML={{ __html: c.motif }} />
        <div className="wrap cat-hero-inner">
          <span className={"tierpill " + (isT1 ? "t1" : "tn")} data-testid="tier-badge">{c.badge}</span>
          <h1 data-testid="category-title">{c.name}</h1>
          <p className="lede">{c.positioning}</p>
          <div className="h-ctas">
            <a className="btn btn-red" href="#requirement" data-testid="cta-send-requirement">Send Us Your Requirement<ArrowRight size={16} /></a>
            <a className="btn rf-btn-outline" href="#process">How It Works</a>
          </div>
          <div className="cat-hero-meta"><span><ShieldCheck size={15} />{tierName}</span><span>{tierNote}</span></div>
        </div>
      </header>

      {/* WHAT WE SOURCE */}
      <section className="cat-section" id="what-we-source">
        <div className="wrap">
          <div className="rf-section-head">
            <div><span className="rf-eyebrow">WHAT WE SOURCE</span><h2>Product range &amp; capability</h2></div>
            <p>What we buy in {c.name.toLowerCase()}, and the standards and controls we work to when we buy it.</p>
          </div>
          <div className="cat-cards">
            <Depth className={"cat-card" + (isT1 ? " t1" : "")} data-reveal>
              <h3>Product range</h3>
              <ul>{c.range.map((r) => <li key={r}><Check size={15} />{r}</li>)}</ul>
            </Depth>
            <Depth className={"cat-card" + (isT1 ? " t1" : "")} data-reveal>
              <h3>Standards &amp; capability</h3>
              <ul>{c.standards.map((r) => <li key={r}><Check size={15} />{r}</li>)}</ul>
            </Depth>
          </div>
          {c.tier === 3 && (
            <p className="cat-note">
              This category runs through vetted specialist partners under the same verification and
              inspection standard we apply everywhere.{" "}
              {c.slug === "medical" && <strong>Certification and import licensing remain the buyer's compliance responsibility.</strong>}
            </p>
          )}
        </div>
      </section>

      {/* WHERE WE SOURCE IT */}
      <section className="cat-section cat-hubs-section" id="where-we-source">
        <div className="wrap">
          <div className="rf-section-head">
            <div><span className="rf-eyebrow">WHERE WE SOURCE IT</span><h2>Which country should make this?</h2></div>
            <p>China is not always the cheapest and India is not always the best. We compare the ecosystem, quality, MOQs, duties and landed cost for your product.</p>
          </div>
          <div className="cat-hub-grid">
            {extras.hubs.map(([hub, note]) => (
              <Depth className="cat-hub" key={hub} data-reveal>
                <MapPin size={19} strokeWidth={1.5} />
                <h3>{hub}</h3>
                <p>{note}</p>
              </Depth>
            ))}
          </div>
          <div className="at-table-scroll">
            <table className="srctable" data-testid="source-table">
              <thead><tr><th>Country</th><th>Strength</th><th>Typical fit</th></tr></thead>
              <tbody>{c.countries.map((row) => (
                <tr key={row.name}><td>{row.name}</td><td>{row.strength}</td><td>{row.fit}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CONSIDERATIONS + VERIFICATION */}
      <section className="cat-section" id="considerations">
        <div className="wrap cat-two">
          <div data-reveal>
            <span className="rf-eyebrow">KEY SOURCING CONSIDERATIONS</span>
            <h2>What decides the price and the outcome.</h2>
            <ul className="cat-list">{extras.considerations.map((t) => <li key={t}><span /><p>{t}</p></li>)}</ul>
          </div>
          <Depth className="cat-verify" data-reveal>
            <span className="rf-eyebrow">QUALITY &amp; VERIFICATION</span>
            <h3>Before you pay a deposit.</h3>
            <ul>{extras.verification.map((t) => <li key={t}><ShieldCheck size={16} />{t}</li>)}</ul>
            <Link className="rf-link" to="/contact?intent=verification" data-testid="category-verification-cta">Verify a supplier in this category<ArrowRight size={15} /></Link>
          </Depth>
        </div>
      </section>

      {/* PROCESS */}
      <section className="cat-section cat-process" id="process">
        <div className="wrap">
          <div className="rf-section-head">
            <div><span className="rf-eyebrow">THE PROCESS</span><h2>From requirement to shipment.</h2></div>
            <p>One accountable partner from the first brief to the delivered container.</p>
          </div>
          <div className="cat-steps">{PROCESS.map(([n, h, p]) => (
            <Depth className="cat-step" key={n} data-reveal><span>{n}</span><h3>{h}</h3><p>{p}</p></Depth>
          ))}</div>
        </div>
      </section>

      {/* RELATED GUIDES + SERVICES */}
      <section className="cat-section cat-related" id="related">
        <div className="wrap cat-two">
          <div data-reveal>
            <span className="rf-eyebrow">RELATED ATLASES &amp; GUIDES</span>
            <h2>Read before you buy.</h2>
            <div className="cat-guide-list">{extras.guides.map(([gslug, title]) => (
              <Link className="cat-guide" to={`/guides/${gslug}`} key={gslug} data-testid={`category-guide-${gslug}`}>
                <BookOpen size={18} strokeWidth={1.5} /><strong>{title}</strong><ArrowUpRight size={17} />
              </Link>
            ))}</div>
          </div>
          <div data-reveal>
            <span className="rf-eyebrow">RELATED SOURCING SERVICES</span>
            <h2>How we can help.</h2>
            <div className="cat-guide-list">{CATEGORY_SERVICES.map(([id, title, desc]) => (
              <Link className="cat-guide cat-service" to={`/services#${id}`} key={id} data-testid={`category-service-${id}`}>
                <div><strong>{title}</strong><span>{desc}</span></div><ArrowUpRight size={17} />
              </Link>
            ))}</div>
          </div>
        </div>
      </section>

      {/* CATEGORY RISKS */}
      <section className="cat-section cat-risks">
        <div className="wrap">
          <div className="rf-section-head">
            <div><span className="rf-eyebrow">AVOID THESE</span><h2>Costly mistakes in {c.name.toLowerCase()}.</h2></div>
            <Link className="rf-link" to="/risks">See all costly sourcing mistakes<ArrowRight size={15} /></Link>
          </div>
          <div className="risklist">{catRisks.map(([title, body, conseq]) => (
            <RiskCard key={title} title={title} body={body} consequence={conseq} />
          ))}</div>
        </div>
      </section>

      {/* FAQ */}
      <section className="cat-section cat-faq-section">
        <div className="wrap at-faq">
          <div>
            <span className="rf-eyebrow">FAQ</span>
            <h2>{c.name} questions.</h2>
            <p>What buyers ask us most often in this category.</p>
            <Link className="rf-link" to="/contact" data-testid="category-faq-contact">Ask the sourcing team<ArrowRight size={15} /></Link>
          </div>
          <div>{c.faq.map((f, i) => (
            <details key={f.q} open={i === 0} data-testid={`faq-${i}`}><summary>{f.q}</summary><p>{f.a}</p></details>
          ))}</div>
        </div>
      </section>

      {/* REQUIREMENT CAPTURE */}
      <section id="requirement" className="cat-section">
        <div className="wrap">
          <div className="reqband">
            <div className="reqcopy">
              <span className="rf-eyebrow" style={{ color: "#E8C67A" }}>SEND US YOUR REQUIREMENT</span>
              <h2>Tell us what you need in {c.name.toLowerCase()}</h2>
              <p>Attach a drawing, photo or spec sheet — our team responds with feasibility, a country recommendation and an indicative cost band.</p>
              {isT1 && <p className="goldnote">Tier 1: send your drawing with grade, coating and standard for a manufacturing quote and PPAP scope.</p>}
            </div>
            <div className="reqcard"><LeadForm source="enquiry" defaultCategory={c.name} withFile /></div>
          </div>
        </div>
      </section>
      <CategoryTestimonials slug={c.slug} />
      <RelatedStrip items={[
        { kicker: "Guides", title: "Costly sourcing mistakes", to: "/risks" },
        { kicker: "Categories", title: "Browse all 8+ categories", to: "/categories" },
        { kicker: "Service", title: "Verification & Inspection", to: "/services" },
      ]} />
    </SiteLayout>
  );
}
